<script setup lang="ts">
import { dateFormatter } from '@/helpers/ep/formatters'
import { getWorkOrders } from '@/services/rc/work-orders.service'
import { useWorkOrderFilterStore } from '@/stores/work-order-filters.store'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import type { AxiosError } from 'axios'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{ customerId: number }>()
const { customerId } = props

// For repair request link
const router = useRouter()

const filterStore = useWorkOrderFilterStore()
filterStore.setCustomer(customerId)
const { filters } = storeToRefs(filterStore)

const openingDetails = ref<number>()
const selectedWorkOrder = ref<any>()

const { isFetching, isRefetching, isError, data, error, refetch } = useQuery<any, AxiosError>({
  queryKey: ['work-orders', filters.value],
  queryFn: () => getWorkOrders(filters.value),
  placeholderData: (prev: any) => prev,
})

/**
 * Handles "open" button click (not yet implemented).
 */
const openWorkOrder = async (data: any, index: number) => {
  openingDetails.value = index
  selectedWorkOrder.value = data
}

/**
 * Navigates to a repair request (not yet implemented).
 */
const goToRepairRequest = async (requestNumber: string) => {
  await router.push({ name: 'rc-customer-repair-requests', query: { number: requestNumber } })
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="flex flex-col gap-lg">
      <el-table
        v-loading="isFetching"
        :data="data?.data"
        :stripe="true"
        :empty-text="isFetching ? 'Loading...' : 'No data.'"
      >
        <el-table-column prop="number" label="Work Order #" :min-width="352"></el-table-column>
        <el-table-column
          prop="start_date"
          label="Start Date"
          :formatter="dateFormatter"
          :min-width="112"
        ></el-table-column>
        <el-table-column prop="due_date" label="Due Date" :formatter="dateFormatter" :min-width="112"></el-table-column>
        <el-table-column prop="repair_request_number" label="Repair Request #" :min-width="176">
          <template #default="scope">
            <el-button
              v-if="scope.row.repair_request_number"
              link
              type="primary"
              @click="goToRepairRequest(scope.row.repair_request_number)"
            >
              #{{ scope.row.repair_request_number.slice(-4) }}
            </el-button>
            <span v-else>N/A</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="Status" :min-width="112">
          <template #default="scope">
            <StatusBadge :status="scope.row.status" />
          </template>
        </el-table-column>
        <el-table-column align="center" :min-width="96">
          <template #default="scope">
            <el-button
              link
              type="primary"
              :loading="openingDetails == scope.$index"
              @click="openWorkOrder(scope.row, scope.$index)"
            >
              Open
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="self-end"
        background
        :layout="DEFAULT_PAGINATION_LAYOUT"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
      />
      <WorkOrderDetailsDrawer
        :work-order="selectedWorkOrder"
        @drawer-opened="openingDetails = undefined"
        @drawer-closed="selectedWorkOrder = undefined"
        @repair-request-link-click="goToRepairRequest"
      />
    </div>
  </template>
</template>
